import { useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TestIds } from '../../../../assets/testIds';
import { getFormattedTestId } from '../../Content/ContentCommonFunctions';
import { usePartnerSpecificModalConfigMap } from './PartnerSpecificFormConfig';
import { useNavigationUtils } from '../../../../common/utils/useNavigationUtils';
import OptimusButton from '../../../../common/library/Button/Button';

interface ConfirmAfterExternalEnrollmentCardProps {
  partnerName?: string;
  partnerCode?: string;
  handleClick?: () => void;
  loading?: boolean;
}

export function ConfirmAfterExternalEnrollmentCard({
  partnerName,
  partnerCode,
  handleClick,
  loading = false,
}: ConfirmAfterExternalEnrollmentCardProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { openExternalLink } = useNavigationUtils();

  const partnerSpecificModalConfigMap = usePartnerSpecificModalConfigMap();
  const partnerConfig = useMemo(() => {
    if (!partnerCode || !(partnerCode in partnerSpecificModalConfigMap)) {
      return undefined; // or some default config
    }
    return partnerSpecificModalConfigMap[partnerCode];
  }, [partnerCode, partnerSpecificModalConfigMap]);

  return (
    <Box>
      <Divider sx={{
        mb: theme.spacing(3),
      }} />
      <Box sx={{
        marginBottom: theme.spacing(3),
        color: theme.palette.grey[500],
      }}>
        <Typography component='span'>
          {partnerConfig?.extraStepConfirmationDescription}
        </Typography>
      </Box>
      <Divider sx={{ marginBottom: theme.spacing(3) }} />
      <Box>
        <OptimusButton
          type={'submit'}
          size={'m'}
          buttonText={
            loading ?
              <CircularProgress color="inherit" size={24} /> :
              <Typography component='span' data-testid={`${TestIds.rewardsPage.signUpForm.partnerLinkBtn}-${getFormattedTestId(partnerName || '')}`}
              >
                {t('button.confirmAndActivate')}
              </Typography>
          }
          disabled={loading}
          sx={{
            mt: theme.spacing(3),
          }}
          onClick={handleClick}
        />
        <OptimusButton
          type={'submit'}
          size={'m'}
          customVariant='secondaryGray'
          buttonText={partnerConfig?.extraStepConfirmationBtnText}
          disabled={loading}
          sx={{
            mt: theme.spacing(3),
          }}
          onClick={() => openExternalLink(partnerConfig?.confirmationBtnLink as string)}
        />
      </Box>
    </Box>
  );
}
